import React, { useRef, useEffect, useState, useCallback } from 'react';
import { gsap } from 'gsap';
import styled from 'styled-components';

// Images (unchanged)
const kitchen1 = 'https://images.unsplash.com/photo-1494017690426-08b6a0f5005e?q=80&w=1364&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D';
const feedback1 = 'https://images.unsplash.com/photo-1553034545-32d4cd2168f1?q=80&w=1287&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D';
const task1 = 'https://images.unsplash.com/photo-1506787497326-c2736dde1bef?q=80&w=1292&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D';

const slides = [
  {
    id: 1,
    title: 'Centralized Portfolio Management',
    nav: "Portfolio Management",
    description: 'Full visibility and control over all policies in one powerful dashboard.',
    bgColor: '#ff1e1d',
    progress: '1/3',
    screenshot: kitchen1,
  },
  {
    id: 2,
    title: 'Simplified Policy Administration',
    nav: "Policy Administration",
    description: 'Save time and minimize errors with streamlined policy lifecycle management.',
    bgColor: '#1a3b5e',
    progress: '2/3',
    screenshot: feedback1,
  },
  {
    id: 3,
    title: 'Quotation & Leads Management',
    nav: 'Quotation Management',
    description: 'Convert prospects effortlessly with fast quotes and intelligent lead tracking.',
    bgColor: '#ff1e1d',
    progress: '3/3',
    screenshot: task1,
  },
];

// Styled components (same as before, only Wrapper gets cursor pointer for interaction)
const Wrapper = styled.div`
  position: relative;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  color: white;
  background: ${(props) => props.bgColor || '#FF8886'};
  transition: background 1.2s ease;
  cursor: default; /* Helps detect interaction */
`;

const Container = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
`;

const Slide = styled.div`
  min-width: 100vw;
  height: 100vh;
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  padding: 0 6rem;
  position: relative;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
    padding: 4rem 3rem;
    text-align: center;
    justify-items: center;
  }
`;

const LeftSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  height: 60vh;
  min-height: 500px;
  padding: 4rem 5rem 4rem 6.5rem;
  background: rgba(0, 0, 0, 0.4);
  border-radius: 0.75rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    left: 30px;
    top: 0;
    bottom: 0;
    width: 1px;
    background: rgba(255, 255, 255, 0.3);
  }

  & > span::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: 10%;
    height: 1px;
    background: rgba(255, 255, 255, 0.3);
  }

  & > span:nth-child(2)::before {
    content: '';
    position: absolute;
    left: 30px;
    top: 10%;
    transform: translate(-50%, -50%);
    width: 10px;
    height: 10px;
    background: #b8ff6e;
    border-radius: 50%;
    z-index: 1;
  }

  @media (max-width: 1200px) {
    height: auto;
    min-height: auto;
    padding: 4rem 3rem;
    margin: 0 auto;
    max-width: 800px;
    align-items: center;
    text-align: center;
    justify-content: center;

    &::before,
    & > span::after,
    & > span:nth-child(2)::before {
      display: none;
    }
  }
`;

const RightSection = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4rem 2rem;

  @media (max-width: 1200px) {
    display: none;
  }
`;

const Screenshot = styled.div`
  background: white;
  border-radius: 1.5rem;
  overflow: hidden;
  box-shadow: 0 30px 60px rgba(0, 0, 0, 0.4);
  width: 100%;
  max-width: 600px;
  height: auto;
  max-height: 65vh;
  transition: transform 0.4s ease;

  &:hover {
    transform: translateY(-10px);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
    background: white;
  }
`;

// Other styled components remain the same...
const ProgressIndicator = styled.div`
  font-size: 1rem;
  opacity: 0.7;
  margin-bottom: 3rem;
  letter-spacing: 0.1em;
`;

const Title = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  line-height: 1.15;
  margin: 0 0 3rem 0;
  max-width: 800px;
  color: #f2f2f2;
`;

const Description = styled.p`
  font-size: 1.4rem;
  opacity: 0.85;
  line-height: 1.7;
  max-width: 650px;
  margin: 0 0 4rem 0;
`;

const LearnMore = styled.a`
  font-size: 1.2rem;
  font-weight: 600;
  color: white;
  text-decoration: none;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  display: inline-flex;
  align-items: center;
  gap: 1rem;
  opacity: 0.9;
  transition: opacity 0.3s;

  &:hover {
    opacity: 1;
  }

  &::after {
    content: '→';
    font-size: 1.6rem;
  }
`;

const NavArrow = styled.button`
  position: absolute;
  top: 10%;
  transform: translateY(-50%);
  left: ${(props) => (props.direction === 'left' ? '3rem' : 'auto')};
  right: ${(props) => (props.direction === 'right' ? '3rem' : 'auto')};
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  font-size: 3.5rem;
  width: 70px;
  height: 70px;
  cursor: pointer;
  z-index: 20;
  border-radius: 50%;
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
  }
`;

const TopNav = styled.div`
  position: absolute;
  top: 4rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 4rem;
  z-index: 15;
  flex-wrap: wrap;
  justify-content: center;

  @media (max-width: 1500px) {
    top: 2rem;
    gap: 2rem;
    flex-direction: column;
    align-items: center;
  }

  @media (max-width: 768px) {
    gap: 1.5rem;
  }
`;

const NavItem = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 1.1rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  cursor: pointer;
  opacity: ${(props) => (props.active ? 1 : 0.5)};
  position: relative;
  padding: 0.5rem 0;
  transition: opacity 0.4s ease;

  &:hover {
    opacity: 0.8;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 2px;
    background: #b8ff6e;
    transform: scaleX(${(props) => (props.active ? 1 : 0)});
    transform-origin: left;
    transition: transform 0.4s ease;
  }
`;

const DividerHelper = styled.span`
  position: absolute;
  left: 0;
  width: 100%;
  height: 0;
`;

const DotHelper = styled.span`
  position: absolute;
  left: 0;
  width: 0;
  height: 0;
`;

const WhyNexus = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const containerRef = useRef(null);
  const slidesRef = useRef([]);
  const wrapperRef = useRef(null);
  const autoplayRef = useRef(null);

  const AUTO_PLAY_INTERVAL = 3000; // 6 seconds

  const goToSlide = useCallback((index) => {
    if (!containerRef.current) return;

    const normalizedIndex = (index + slides.length) % slides.length;

    gsap.to(containerRef.current, {
      x: -normalizedIndex * window.innerWidth,
      duration: 1.2,
      ease: 'power3.inOut',
    });

    setCurrentIndex(normalizedIndex);

    gsap.to(wrapperRef.current, {
      backgroundColor: slides[normalizedIndex].bgColor,
      duration: 1.2,
      ease: 'power3.inOut',
    });

    const currentSlide = slidesRef.current[normalizedIndex];
    if (currentSlide) {
      gsap.fromTo(
        currentSlide.querySelectorAll('.animate-in'),
        { opacity: 0, y: 60 },
        { opacity: 1, y: 0, stagger: 0.15, duration: 1, ease: 'power3.out' }
      );

      const screenshot = currentSlide.querySelector('.screenshot-item');
      if (screenshot) {
        gsap.fromTo(
          screenshot,
          { opacity: 0, scale: 0.95, y: 40 },
          { opacity: 1, scale: 1, y: 0, duration: 1.2, ease: 'power3.out' }
        );
      }
    }
  }, []);

  const next = () => goToSlide(currentIndex + 1);
  const prev = () => goToSlide(currentIndex - 1);

  // Autoplay logic
  useEffect(() => {
    if (isPaused) return;

    autoplayRef.current = setInterval(() => {
      next();
    }, AUTO_PLAY_INTERVAL);

    return () => {
      if (autoplayRef.current) clearInterval(autoplayRef.current);
    };
  }, [currentIndex, isPaused, next]);

  // Initial load
  useEffect(() => {
    goToSlide(0);
  }, [goToSlide]);

  return (
    <Wrapper
      ref={wrapperRef}
      bgColor={slides[currentIndex].bgColor}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <NavArrow direction="left" onClick={prev}>‹</NavArrow>
      <NavArrow direction="right" onClick={next}>›</NavArrow>

      <TopNav>
        {slides.map((slide, i) => (
          <NavItem
            key={i}
            active={i === currentIndex}
            onClick={() => goToSlide(i)}
          >
            {slide.nav}
          </NavItem>
        ))}
      </TopNav>

      <Container
        ref={containerRef}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {slides.map((slide, i) => (
          <Slide key={slide.id} ref={(el) => (slidesRef.current[i] = el)}>
            <LeftSection>
              <DividerHelper />
              <DotHelper />
              {slide.progress && <ProgressIndicator className="animate-in">{slide.progress}</ProgressIndicator>}
              <Title className="animate-in">{slide.title}</Title>
              <Description className="animate-in">{slide.description}</Description>
              <LearnMore href="#" className="animate-in">Learn more</LearnMore>
            </LeftSection>

            <RightSection>
              <Screenshot className="screenshot-item">
                <img src={slide.screenshot} alt={`Feature screenshot for ${slide.title}`} />
              </Screenshot>
            </RightSection>
          </Slide>
        ))}
      </Container>
    </Wrapper>
  );
};

export default WhyNexus;